// plugins/chatbot.ts;
export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const insyncChatStylesheetUrl = "https://dbrd449anfbv4.cloudfront.net/insync_chat_button.css";
  const insyncChatStylesheetReadyAttribute = "data-insync-chat-stylesheet-ready";

  const waitForInsyncChatStylesheet = (link: HTMLLinkElement, resolve: () => void): void => {
    const markReady = () => {
      link.setAttribute(insyncChatStylesheetReadyAttribute, "true");
      resolve();
    };

    if (link.getAttribute(insyncChatStylesheetReadyAttribute) === "true" || link.sheet) {
      markReady();
      return;
    }

    link.addEventListener("load", markReady, { once: true });
    link.addEventListener("error", markReady, { once: true });
  };

  const loadInsyncChatStylesheet = () => {
    return new Promise<void>((resolve) => {
      const existingStylesheet = document.querySelector<HTMLLinkElement>(
        `link[href="${insyncChatStylesheetUrl}"]`
      );

      if (existingStylesheet) {
        waitForInsyncChatStylesheet(existingStylesheet, resolve);
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = insyncChatStylesheetUrl;
      waitForInsyncChatStylesheet(link, resolve);
      document.head.appendChild(link);
    });
  };

  /** -------------------------
   * GA CLIENT & SESSION HELPERS
   * ------------------------- **/
  function getGaClientId() {
    const match = document.cookie.match(/_ga=(.+?);/);
    if (!match) return null;
    try {
      return match[1].split(".").slice(-2).join(".");
    } catch {
      return null;
    }
  }

  function waitForGaSessionId(callback: any, retries = 20) {
    const pattern = /_ga_TEHJR60KD9=GS\d\.\d\.(.+?)(?:;|$)/;
    const match = document.cookie.match(pattern);
    const parts = match?.[1]?.split(".");

    if (parts) {
      const gaSessionId = parts.shift();
      callback(gaSessionId);
      return;
    }

    if (retries > 0) {
      setTimeout(() => waitForGaSessionId(callback, retries - 1), 200);
    } else {
      console.warn("GA session ID not found after multiple retries — continuing without it.");
      callback(null); // Allow chatbot to initialize without GA session
    }
  }

  // Define chatbot HTML (iframe + chat icon)
  const chatbotHtml = `
      <iframe
        id="insync-iframe"
        src="https://dbrd449anfbv4.cloudfront.net/insync_iframe_webchat_js_${
          config.public.siteEnv === "prod" ? "prod" : "staging"
        }.html"
        class="responsive-iframe"
        title="insync-chatbot"
        sandbox='allow-scripts allow-modals allow-popups allow-same-origin allow-popups-to-escape-sandbox' allow='clipboard-write;microphone;'
      ></iframe>`;
  // Inject the chatbot HTML into the body
  const body = document.body;
  window.chatBotScriptLoaded = false;
  if (body) {
    loadInsyncChatStylesheet().then(() => {
      body.insertAdjacentHTML("beforeend", chatbotHtml);

      // Dynamically load the script
      const script = document.createElement("script");
      script.src = `https://dbrd449anfbv4.cloudfront.net/insync_webchat_${
        config.public.siteEnv === "prod" ? "production" : "staging"
      }.js`;
      script.async = true;

      script.onload = () => {
        console.log("Chatbot script loaded");
        waitForGaSessionId((gaSessionId: any) => {
          const gaClientId = getGaClientId();
          console.log("GA IDs:", { gaClientId, gaSessionId });

          // Set up the chatbot environment once the script is loaded
          if (window.insyncSetChatEnv) {
            window.chatBotScriptLoaded = true;

            window.insyncSetChatEnv({
              insyncMarket: "us_asu",
              insyncEnv: config.public.siteEnv === "prod" ? "production" : "staging",
              insyncAppIdDev: "702c1e15c41a13c70bd2b181f6a98cc1",
              insyncAppIdStage: "478b2581c1863bac4fab00cf95a39309",
              insyncAppIdProd: "48bb0fcb7b33931c620b52d6a452a053",
              insyncUserIdExpiryInDays: 30,
              insyncChatIconId: "insync-chat-icon-id",
              insyncIframeId: "insync-iframe",
              insyncHostedPageRef: window.location.href,
              insyncContextParams: {
                GAClientId__c: gaClientId,
                GA_Session_ID__c: gaSessionId
              }
            });
          }
        });
      };

      script.onerror = (error) => {
        console.error("Failed to load chatbot script", error);
      };

      document.head.appendChild(script);
    });
  }

  // Define the openChat function on the window object
  window.openChat = () => {
    if (window.insyncSetChatEnv) {
      // Make sure you're passing the correct configuration object to openChat
      window.insyncSetChatEnv.openChat("OpenChat", "user", {
        insyncMarket: "us_asu",
        insyncEnv: config.public.siteEnv === "prod" ? "production" : "staging",
        insyncAppIdDev: "702c1e15c41a13c70bd2b181f6a98cc1",
        insyncAppIdStage: "478b2581c1863bac4fab00cf95a39309",
        insyncAppIdProd: "48bb0fcb7b33931c620b52d6a452a053",
        insyncUserIdExpiryInDays: 30,
        insyncChatIconId: "insync-chat-icon-id",
        insyncIframeId: "insync-iframe",
        insyncHostedPageRef: window.location.href
      });
    } else {
      console.error("Chatbot environment is not set properly.");
    }
  };
});
